import {
  Controls,
  ReactFlow,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import ChainCardNode from "./ChainCardNode";
import ChainEdge from "./ChainEdge";
import { CARD_SIZE, ChainNode, Position, Size } from "./chainLayout";
import {
  SUBSYSTEM_SIZE,
  TREE_CARD_SIZE,
  TreeLayout,
  placeTree,
  subsystemNodeId,
  toTreeEdges,
  treeLayout,
} from "./treeLayout";
import { VisibleTree } from "./treeModel";

const NODE_TYPES = { chainCard: ChainCardNode };
const EDGE_TYPES = { chainEdge: ChainEdge };

// The space kept round what is framed, and the zooms a frame stays between:
// below the smallest the cards cannot be read, and the frame starts at its
// top instead of fitting
const FRAME_PAD = 32;
const FRAME_MIN_ZOOM = 0.55;
const FRAME_MAX_ZOOM = 1;

type Box = Position & Size;

// What a layout frames: the open subsystem, else the root and every
// subsystem
const frameOf = (
  visible: VisibleTree,
  layout: TreeLayout,
  sizes: ReadonlyMap<string, Size>,
): Box | null => {
  if (layout.branch) {
    return layout.branch;
  }
  const boxes = [
    { id: visible.tree.root.id, fallback: CARD_SIZE },
    ...visible.tree.subsystems.map((subsystem) => ({
      id: subsystemNodeId(subsystem.name),
      fallback: SUBSYSTEM_SIZE,
    })),
  ].flatMap(({ id, fallback }) => {
    const at = layout.positions.get(id);
    return at ? [{ ...at, ...(sizes.get(id) ?? fallback) }] : [];
  });
  if (boxes.length === 0) {
    return null;
  }
  const x = Math.min(...boxes.map((box) => box.x));
  const y = Math.min(...boxes.map((box) => box.y));
  return {
    x,
    y,
    width: Math.max(...boxes.map((box) => box.x + box.width)) - x,
    height: Math.max(...boxes.map((box) => box.y + box.height)) - y,
  };
};

export interface TreeCanvasProps {
  tree: VisibleTree;
  // A kind to bring into view, once shown on request
  focus?: string | null;
}

// The canvas of the tree view: the root, the subsystems and the window of
// the open one, at the positions of the tree's layout. The layout runs once
// for each tree it is given, and once more when the cards have been
// measured; each time the view frames the open subsystem, or the root and
// the subsystems when none is open. It is drawn inside the page's canvas
// provider, so that the toolbar can fit and export it.
const TreeCanvas: React.FC<TreeCanvasProps> = ({ tree, focus = null }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<ChainNode>([]);
  const edges = useMemo(() => toTreeEdges(tree), [tree]);
  const { getNodes, setViewport } = useReactFlow<ChainNode>();
  const store = useStoreApi<ChainNode>();
  const initialized = useNodesInitialized();
  // Whether the layout of this tree has used the cards' measured sizes
  const measuredLayout = useRef(false);
  // The measured size of each node met so far
  const sizes = useRef(new Map<string, Size>());
  // The tree laid out last
  const laidOut = useRef(tree);
  // The kind to bring into view, read by the frame without running it again
  const focused = useRef(focus);
  focused.current = focus;

  const frame = useCallback(
    (visible: VisibleTree, layout: TreeLayout) => {
      const { width, height } = store.getState();
      const box = frameOf(visible, layout, sizes.current);
      if (!width || !height || !box || box.width <= 0 || box.height <= 0) {
        return;
      }
      const zoom = Math.min(
        FRAME_MAX_ZOOM,
        Math.max(
          FRAME_MIN_ZOOM,
          Math.min(
            (width - 2 * FRAME_PAD) / box.width,
            (height - 2 * FRAME_PAD) / box.height,
          ),
        ),
      );
      // Centred when it fits, from its top-left corner when it does not; from
      // its top-right corner when the tree grows leftwards, where its first
      // level is
      const along = (size: number, room: number, start: number, fromEnd = false) =>
        size * zoom <= room - 2 * FRAME_PAD
          ? (room - size * zoom) / 2 - start * zoom
          : fromEnd
            ? room - FRAME_PAD - (start + size) * zoom
            : FRAME_PAD - start * zoom;
      let x = along(box.width, width, box.x, layout.mirrored);
      let y = along(box.height, height, box.y);
      // A kind shown on request is brought into the middle when the frame
      // leaves it out
      const id = focused.current;
      const at = id ? layout.positions.get(id) : undefined;
      if (id && at) {
        const size = sizes.current.get(id) ?? TREE_CARD_SIZE;
        const left = at.x * zoom + x;
        const top = at.y * zoom + y;
        if (
          left < 0 ||
          top < 0 ||
          left + size.width * zoom > width ||
          top + size.height * zoom > height
        ) {
          x = width / 2 - (at.x + size.width / 2) * zoom;
          y = height / 2 - (at.y + size.height / 2) * zoom;
        }
      }
      void setViewport({ x, y, zoom }, { duration: 200 });
    },
    [store, setViewport],
  );

  // Once per tree: the layout with the sizes known so far
  useEffect(() => {
    const layout = treeLayout(tree, sizes.current);
    laidOut.current = tree;
    measuredLayout.current = false;
    setNodes(placeTree(tree, layout));
    window.requestAnimationFrame(() => frame(tree, layout));
  }, [tree, setNodes, frame]);

  // Once more when the nodes have been measured, if a size has changed
  useEffect(() => {
    if (!initialized || measuredLayout.current) {
      return;
    }
    const shown = laidOut.current;
    measuredLayout.current = true;
    let changed = false;
    for (const node of getNodes()) {
      const { width, height } = node.measured ?? {};
      if (!width || !height || node.data.variant === "branch") {
        continue;
      }
      const known = sizes.current.get(node.id);
      if (!known || known.width !== width || known.height !== height) {
        sizes.current.set(node.id, { width, height });
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    const layout = treeLayout(shown, sizes.current);
    const fresh = new Map(
      placeTree(shown, layout).map((node): [string, ChainNode] => [node.id, node]),
    );
    // The cards keep their measures and move; the subsystem's box is drawn
    // afresh
    setNodes((current) =>
      current.map((node) =>
        node.data.variant === "branch"
          ? (fresh.get(node.id) ?? node)
          : { ...node, position: layout.positions.get(node.id) ?? node.position },
      ),
    );
    window.requestAnimationFrame(() => frame(shown, layout));
  }, [initialized, getNodes, setNodes, frame]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      nodesDraggable={false}
      nodesConnectable={false}
      minZoom={0.2}
      maxZoom={1.5}
      colorMode="dark"
      proOptions={{ hideAttribution: true }}
      style={{ backgroundColor: "transparent" }}
    >
      <Controls showInteractive={false} showFitView={false} />
    </ReactFlow>
  );
};

export default TreeCanvas;
